import { ThemeEvents } from '@theme/events';
import { StandardEvents } from '@shopify/events';

/**
 * Köttbutiken: klistrad köprad (sections/kb-sticky-kop.liquid).
 * Raden har ingen egen varukorgslogik: den väljer variant och antal i produktinfons formulär och
 * klickar på dess köpknapp, så att tillval, varukorgslådan och felmeddelanden fungerar som vanligt.
 * Visas när produktinfons köpknappar har scrollats förbi (även vid sidfoten, som förebilden;
 * sidfoten får luft under sig i stället).
 */
class KbStickyKop extends HTMLElement {
  #avbryt = new AbortController();
  /** @type {IntersectionObserver[]} */
  #observatorer = [];
  #forbi = false;
  /** @type {ResizeObserver | undefined} */
  #hojd;
  #iRedigeraren = false;

  /** @type {Map<string, any>} */
  #varianter = new Map();

  connectedCallback() {
    const data = this.querySelector('script[data-kb-varianter]');
    try {
      for (const variant of JSON.parse(data?.textContent || '[]')) this.#varianter.set(String(variant.id), variant);
    } catch {
      /* ogiltig JSON – raden fungerar ändå med vald variant */
    }

    const { signal } = this.#avbryt;
    this.#formular?.closest('.shopify-section')?.addEventListener(StandardEvents.productSelect, this.#vidVariantbyte, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#vidAntalIFormularet, { signal });

    this.querySelector('[data-kb-variant]')?.addEventListener('change', this.#vidVal, { signal });
    this.querySelector('[data-kb-minus]')?.addEventListener('click', () => this.#stegaAntal(-1), { signal });
    this.querySelector('[data-kb-plus]')?.addEventListener('click', () => this.#stegaAntal(1), { signal });
    this.querySelector('[data-kb-antal]')?.addEventListener('change', () => this.#satAntal(this.#antal), { signal });
    this.querySelector('[data-kb-kop]')?.addEventListener('click', this.#kop, { signal });

    document.addEventListener('shopify:section:select', this.#vidSektionsval, { signal });
    document.addEventListener('shopify:section:deselect', this.#vidSektionsval, { signal });

    this.#bevaka();
    this.#satAntal(this.#antal);
  }

  disconnectedCallback() {
    this.#avbryt.abort();
    this.#observatorer.forEach((o) => o.disconnect());
    this.#hojd?.disconnect();
  }

  /* ---------- Produktinfons formulär ---------- */

  /** @returns {HTMLElement | null} */
  get #formular() {
    const formular = document.querySelectorAll(`product-form-component[data-product-id="${this.dataset.productId}"]`);
    return /** @type {HTMLElement | null} */ (
      [...formular].find((f) => !f.closest('product-card, dialog, kb-sticky-kop')) ?? null
    );
  }

  /** @returns {HTMLElement | null} */
  get #variantvaljare() {
    return this.#formular?.closest('.shopify-section')?.querySelector(`variant-picker[data-product-id="${this.dataset.productId}"]`) ?? null;
  }

  get #rad() {
    return /** @type {HTMLElement} */ (this.querySelector('.kb-sticky-kop__rad'));
  }

  /* ---------- Synlighet ---------- */

  #bevaka() {
    const formular = this.#formular;
    const kopdel = formular?.closest('.buy-buttons-block') ?? formular;
    if (!kopdel) return;

    // Köpknapparna räknas som förbi när de har gått in under den klistrade headern.
    const header = parseInt(getComputedStyle(document.body).getPropertyValue('--header-height'), 10) || 0;
    const knappar = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        this.#forbi = !entry.isIntersecting && entry.boundingClientRect.bottom < header + 1;
        this.#uppdateraSynlighet();
      },
      { rootMargin: `-${header}px 0px 0px 0px` }
    );
    knappar.observe(kopdel);
    this.#observatorer.push(knappar);

    // Radens höjd som variabel, så att sidfoten får lika mycket luft under sig.
    this.#hojd = new ResizeObserver(() => {
      document.documentElement.style.setProperty('--kb-sticky-hojd', `${this.#rad.offsetHeight}px`);
    });
    this.#hojd.observe(this.#rad);
  }

  #uppdateraSynlighet() {
    const synlig = this.#iRedigeraren || this.#forbi;
    this.#rad.dataset.synlig = String(synlig);
    this.#rad.toggleAttribute('inert', !synlig);
  }

  /** I temaredigeraren: visa raden när sektionen är markerad. */
  #vidSektionsval = (/** @type {Event} */ event) => {
    if (!(event.target instanceof Element) || !event.target.contains(this)) return;
    this.#iRedigeraren = event.type === 'shopify:section:select';
    this.#uppdateraSynlighet();
  };

  /* ---------- Variant ---------- */

  /** Kunden väljer variant i raden → välj samma i produktinfon. */
  #vidVal = (/** @type {Event} */ event) => {
    const id = /** @type {HTMLSelectElement} */ (event.target).value;
    this.#visaVariant(id);
    this.#valjIFormularet(id);
  };

  /** @param {string} id */
  #valjIFormularet(id) {
    const variant = this.#varianter.get(id);
    const valjare = this.#variantvaljare;

    if (!variant || !valjare) {
      // Ingen synlig väljare i produktinfon: sätt variant-id direkt.
      const falt = /** @type {HTMLInputElement | null} */ (this.#formular?.querySelector('input[name="id"]'));
      if (falt) falt.value = id;
      return;
    }

    // Byt ett alternativ i taget, i samma ordning som produktens alternativ. Väljaren hämtar
    // nästa vy själv; byten som köas under tiden hanteras av produktformuläret.
    /** @type {string[]} */ (variant.options).forEach((varde) => {
      const radio = /** @type {HTMLInputElement | null} */ (
        valjare.querySelector(`input[type="radio"][value="${CSS.escape(varde)}"]`)
      );
      if (radio) {
        if (!radio.checked) {
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        }
        return;
      }

      const alternativ = /** @type {HTMLOptionElement | null} */ (
        valjare.querySelector(`select option[value="${CSS.escape(varde)}"]`)
      );
      const lista = alternativ?.closest('select');
      if (alternativ && lista && lista.value !== varde) {
        lista.value = varde;
        lista.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  /** Variant vald i produktinfon → visa den i raden. */
  #vidVariantbyte = (/** @type {any} */ event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card, kb-sticky-kop')) return;

    event.promise
      ?.then((/** @type {any} */ { detail }) => {
        if (detail?.productId && String(detail.productId) !== this.dataset.productId) return;
        const id = detail?.resource?.id;
        if (id) this.#visaVariant(String(id));
        else this.#visaEjTillganglig();
      })
      .catch(() => {});
  };

  /** @param {string} id */
  #visaVariant(id) {
    const variant = this.#varianter.get(id);
    if (!variant) return;

    this.dataset.variantId = id;

    const lista = /** @type {HTMLSelectElement | null} */ (this.querySelector('[data-kb-variant]'));
    if (lista && lista.value !== id) lista.value = id;

    const pris = this.querySelector('[data-kb-pris]');
    if (pris) pris.innerHTML = variant.pris;

    const bild = /** @type {HTMLImageElement | null} */ (this.querySelector('[data-kb-bild]'));
    if (bild && variant.bild) {
      bild.srcset = '';
      bild.src = variant.bild;
    }

    const falt = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-kb-antal]'));
    if (falt) {
      falt.min = String(variant.min || 1);
      falt.step = String(variant.steg || 1);
      if (variant.max) falt.max = String(variant.max);
      else falt.removeAttribute('max');
      this.#satAntal(this.#antal);
    }

    this.#satKopbar(Boolean(variant.available));
  }

  #visaEjTillganglig() {
    this.#satKopbar(false);
  }

  /** @param {boolean} kopbar */
  #satKopbar(kopbar) {
    const knapp = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-kb-kop]'));
    if (!knapp) return;
    knapp.disabled = !kopbar;
    const text = knapp.querySelector('[data-kb-knapptext]');
    if (text) text.textContent = (kopbar ? knapp.dataset.textKop : knapp.dataset.textSlut) ?? '';
  }

  /* ---------- Antal ---------- */

  get #antal() {
    const falt = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-kb-antal]'));
    return falt ? Number(falt.value) || 1 : 1;
  }

  /** @param {number} riktning */
  #stegaAntal(riktning) {
    const falt = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-kb-antal]'));
    const steg = Number(falt?.step) || 1;
    this.#satAntal(this.#antal + riktning * steg);
  }

  /** @param {number} varde */
  #satAntal(varde) {
    const falt = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-kb-antal]'));
    if (!falt) return;
    const min = Number(falt.min) || 1;
    const max = Number(falt.max) || Infinity;
    const antal = Math.min(Math.max(Math.round(varde) || min, min), max);
    falt.value = String(antal);

    const minus = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-kb-minus]'));
    const plus = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-kb-plus]'));
    if (minus) minus.disabled = antal <= min;
    if (plus) plus.disabled = antal >= max;
  }

  /** Antalet ändrat i produktinfon → samma i raden. */
  #vidAntalIFormularet = (/** @type {any} */ event) => {
    if (event.detail?.cartLine) return;
    const antal = Number(event.detail?.quantity);
    if (antal) this.#satAntal(antal);
  };

  /* ---------- Köp ---------- */

  #kop = () => {
    const formular = this.#formular;
    const knapp = /** @type {HTMLButtonElement | null} */ (formular?.querySelector('[ref="addToCartButton"]'));
    const egenKnapp = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-kb-kop]'));
    if (!formular || !knapp || !egenKnapp || egenKnapp.disabled) return;

    // Samma variant i produktinfon som i raden (om kunden bytt utan att väljaren hunnit följa med).
    const id = this.dataset.variantId;
    const idFalt = /** @type {HTMLInputElement | null} */ (formular.querySelector('input[name="id"]'));
    if (id && idFalt && idFalt.value !== id) this.#valjIFormularet(id);

    // Antal: produktinfons antalsväljare om den finns, annars ett dolt fält i formuläret.
    if (this.querySelector('[data-kb-antal]')) {
      const antal = String(this.#antal);
      const valjare = /** @type {any} */ (formular.querySelector('quantity-selector-component'));
      if (valjare?.setValue) {
        valjare.setValue(antal);
      } else {
        const form = formular.querySelector('form');
        let falt = /** @type {HTMLInputElement | null} */ (form?.querySelector('input[name="quantity"]'));
        if (!falt && form) {
          falt = document.createElement('input');
          falt.type = 'hidden';
          falt.name = 'quantity';
          form.append(falt);
        }
        if (falt) falt.value = antal;
      }
    }

    egenKnapp.setAttribute('aria-busy', 'true');
    const klart = () => {
      egenKnapp.removeAttribute('aria-busy');
      knapp.dataset.puppet = 'false';
    };
    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      (/** @type {any} */ event) => (event.promise ? event.promise.finally(klart) : klart()),
      { once: true }
    );
    document.addEventListener(StandardEvents.cartError, klart, { once: true });
    window.setTimeout(klart, 8000);

    // "puppet" = produktformuläret hoppar över sin egen knappanimation (Horizons mönster).
    knapp.dataset.puppet = 'true';
    knapp.click();
  };
}

if (!customElements.get('kb-sticky-kop')) {
  customElements.define('kb-sticky-kop', KbStickyKop);
}
